import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Box, Button, Typography, IconButton, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getWidgetTabsConfig } from './widgetTabsConfig';
import { buildPropsFromFormData, buildWidgetSettings } from './propsBuilder';
import { buildInitialData } from './initialDataBuilder';
import AppearanceTab from './tabs/AppearanceTab';

// ─── Step components (same registry as WidgetSetupWizard) ─────────────────
import TemplateSplitStep from '../WidgetSetupWizard/steps/TemplateSplitStep';
import MoadCalcStep          from '../WidgetSetupWizard/steps/MoadCalcStep';
import MoadDaysStep          from '../WidgetSetupWizard/steps/MoadDaysStep';
import MoadZmanimPerDayStep  from '../WidgetSetupWizard/steps/MoadZmanimPerDayStep';
import ShabbatCalcStep       from '../WidgetSetupWizard/steps/ShabbatCalcStep';
import PrayerListStep        from '../WidgetSetupWizard/steps/PrayerListStep';
import CustomTimesStep       from '../WidgetSetupWizard/steps/CustomTimesStep';
import DrashotStep           from '../WidgetSetupWizard/steps/DrashotStep';
import MoadDrashotStep       from '../WidgetSetupWizard/steps/MoadDrashotStep';
import TextContentStep       from '../WidgetSetupWizard/steps/TextContentStep';
import ImageStep             from '../WidgetSetupWizard/steps/ImageStep';
import GenericListStep       from '../WidgetSetupWizard/steps/GenericListStep';
import EmergencyStep         from '../WidgetSetupWizard/steps/EmergencyStep';
import NitzavStep            from '../WidgetSetupWizard/steps/NitzavStep';
import FinancialItemsStep    from '../WidgetSetupWizard/steps/FinancialItemsStep';
import ParnasTypeStep        from '../WidgetSetupWizard/steps/ParnasTypeStep';
import SponsorsStep          from '../WidgetSetupWizard/steps/SponsorsStep';
import ZmanimVisibilityStep  from '../WidgetSetupWizard/steps/ZmanimVisibilityStep';
import OmerSettingsStep      from '../WidgetSetupWizard/steps/OmerSettingsStep';
import TimelineValidityStep  from '../WidgetSetupWizard/steps/TimelineValidityStep';
import LeyningSettingsStep   from '../WidgetSetupWizard/steps/LeyningSettingsStep';
import InyaneiSettingsStep   from '../WidgetSetupWizard/steps/InyaneiSettingsStep';
import LimudimSettingsStep   from '../WidgetSetupWizard/steps/LimudimSettingsStep';
import ZmanimInyaneiStep     from '../WidgetSetupWizard/steps/ZmanimInyaneiStep';
import SeatingMapStep        from '../WidgetSetupWizard/steps/SeatingMapStep';
import DocumentStep          from '../WidgetSetupWizard/steps/DocumentStep';
import ClockSettingsStep     from './tabs/ClockSettingsStep';
import ZmanimDesignStep      from './tabs/ZmanimDesignStep';
import SimchaGreetingsTab    from './tabs/SimchaGreetingsTab';

const STEP_COMPONENTS = {
  TemplateSplitStep,
  MoadCalcStep,
  MoadDaysStep,
  MoadZmanimPerDayStep,
  ShabbatCalcStep,
  PrayerListStep,
  CustomTimesStep,
  DrashotStep,
  MoadDrashotStep,
  TextContentStep,
  ImageStep,
  GenericListStep,
  EmergencyStep,
  NitzavStep,
  FinancialItemsStep,
  ParnasTypeStep,
  SponsorsStep,
  ZmanimVisibilityStep,
  OmerSettingsStep,
  TimelineValidityStep,
  LeyningSettingsStep,
  InyaneiSettingsStep,
  LimudimSettingsStep,
  ZmanimInyaneiStep,
  SeatingMapStep,
  DocumentStep,
  ClockSettingsStep,
  ZmanimDesignStep,
  SimchaGreetingsTab,
  AppearanceTab,
};

// ─── Theme ──────────────────────────────────────────────────────────────────
const rtlTheme = createTheme({ direction: 'rtl' });
const navy = '#1e3a5f';

// ─── Main Component ─────────────────────────────────────────────────────────
const WidgetConfigurator = ({
  open,
  componentKey,
  widgetConfig,
  isEditMode = false,
  initialData = null,
  onSave,
  onClose,
  onUploadImage,
  onFetchZmanimForDay,
  onFetchTodayZmanim,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [todayZmanim, setTodayZmanim] = useState(null);
  const [showDescription, setShowDescription] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open && componentKey) {
      const config = getWidgetTabsConfig(componentKey);
      if (!config) return;

      if (isEditMode && initialData) {
        // Edit mode: build from existing widget
        setFormData(buildInitialData(componentKey, {
          props: initialData.props || {},
          widgetSettings: initialData.widgetSettings || {},
        }));
        setSelectedTemplateId(initialData.templateId || widgetConfig?.templates?.[0]?.id || '');
      } else {
        // New widget: use defaults
        setFormData({ ...config.defaultData });
        setSelectedTemplateId(widgetConfig?.templates?.[0]?.id || '');
      }
      setActiveTab(0);
      setShowDescription(false);

      if (onFetchTodayZmanim) {
        onFetchTodayZmanim().then(setTodayZmanim).catch(() => {});
      }
    }
  }, [open, componentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((partial) => {
    setFormData(prev => ({ ...prev, ...partial }));
  }, []);

  const handleSave = () => {
    const props = buildPropsFromFormData(componentKey, formData);
    const settings = buildWidgetSettings(formData);
    onSave(componentKey, props, selectedTemplateId, settings);
  };

  const config = getWidgetTabsConfig(componentKey);
  if (!config) return null;

  const hasTemplate = config.hasTemplate !== false && widgetConfig?.templates?.length > 0;
  const showTitleSection = config.showTitleSection !== false;

  // Build tab list: widget-specific tabs + appearance + template
  const allTabs = [
    ...config.tabs,
    { id: 'appearance', label: 'עיצוב', componentName: 'AppearanceTab' },
    ...(hasTemplate ? [{ id: 'template', label: 'תבנית', componentName: 'TemplateSplitStep' }] : []),
  ];

  // Render tab content
  const renderTabContent = () => {
    const tab = allTabs[activeTab];
    if (!tab) return null;

    if (tab.componentName === 'AppearanceTab') {
      return (
        <AppearanceTab
          formData={formData}
          onChange={handleChange}
          showTitleSection={showTitleSection}
          componentKey={componentKey}
        />
      );
    }

    if (tab.componentName === 'TemplateSplitStep') {
      return (
        <TemplateSplitStep
          componentKey={componentKey}
          templates={widgetConfig?.templates || []}
          selectedTemplateId={selectedTemplateId}
          onSelectTemplate={setSelectedTemplateId}
          formData={formData}
          onChange={handleChange}
        />
      );
    }

    const StepComponent = STEP_COMPONENTS[tab.componentName];
    if (!StepComponent) {
      return (
        <Box sx={{ p: 3, textAlign: 'center', color: '#9ca3af' }}>
          שלב זה בפיתוח ({tab.componentName})
        </Box>
      );
    }

    return (
      <StepComponent
        formData={formData}
        onChange={handleChange}
        widgetConfig={widgetConfig}
        onUploadImage={onUploadImage}
        onFetchZmanimForDay={onFetchZmanimForDay}
        todayZmanim={todayZmanim}
        {...(tab.props || {})}
      />
    );
  };

  return (
    <ThemeProvider theme={rtlTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: 1400,
            height: '88vh',
            maxHeight: '92vh',
            bgcolor: '#ffffff',
            borderRadius: '16px',
            m: { xs: 1, sm: 2 },
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.16)',
          },
        }}
      >
        <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ──── Header ──── */}
          <Box sx={{
            px: 3, py: 2,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid rgba(30,58,95,0.08)',
            background: 'linear-gradient(135deg, rgba(30,58,95,0.03) 0%, transparent 80%)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '1.3rem' }}>{config.intro.emoji}</Typography>
              <Typography sx={{ color: navy, fontWeight: 700, fontSize: '1.05rem' }}>
                {isEditMode ? `עריכת ${config.intro.title}` : config.intro.title}
              </Typography>
              {config.intro.description && (
                <IconButton
                  size="small"
                  onClick={() => setShowDescription(!showDescription)}
                  sx={{
                    color: '#9ca3af', width: 28, height: 28,
                    '&:hover': { color: navy, bgcolor: 'rgba(30,58,95,0.06)' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: '#6b7280', bgcolor: 'rgba(30,58,95,0.05)', borderRadius: '8px',
                width: 32, height: 32,
                '&:hover': { bgcolor: 'rgba(30,58,95,0.10)', color: navy },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Description tooltip */}
          {showDescription && (
            <Box sx={{
              px: 3, py: 1.5,
              bgcolor: 'rgba(30,58,95,0.03)',
              borderBottom: '1px solid rgba(30,58,95,0.06)',
              flexShrink: 0,
            }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.7 }}>
                {config.intro.description}
              </Typography>
            </Box>
          )}

          {/* ──── Body: Sidebar + Content ──── */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* Sidebar tabs */}
            <Box sx={{
              width: 190,
              flexShrink: 0,
              borderLeft: '2px solid rgba(30,58,95,0.10)',
              bgcolor: '#f4f6f9',
              boxShadow: 'inset -6px 0 12px rgba(30,58,95,0.04)',
              display: 'flex',
              flexDirection: 'column',
              py: 2,
              overflowY: 'auto',
            }}>
              {allTabs.map((tab, index) => {
                const isActive = activeTab === index;
                const isSeparator = index === config.tabs.length; // before appearance tab
                return (
                  <React.Fragment key={tab.id}>
                    {isSeparator && config.tabs.length > 0 && (
                      <Box sx={{ mx: 2, my: 1.5, borderTop: '1px solid rgba(30,58,95,0.12)' }} />
                    )}
                    <Box
                      onClick={() => setActiveTab(index)}
                      sx={{
                        px: 2.5, py: 1.4,
                        mx: 1, mb: 0.5,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.86rem',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? 'white' : '#4b5563',
                        bgcolor: isActive ? navy : 'transparent',
                        boxShadow: isActive ? '0 2px 8px rgba(30,58,95,0.25)' : 'none',
                        transition: 'all 0.15s',
                        '&:hover': {
                          bgcolor: isActive ? navy : 'rgba(30,58,95,0.08)',
                          color: isActive ? 'white' : navy,
                        },
                      }}
                    >
                      {tab.label}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            {/* Content area */}
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 2, sm: 4 },
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#e5e7eb', borderRadius: 3 },
            }}>
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {renderTabContent()}
              </Box>
            </Box>
          </Box>

          {/* ──── Footer ──── */}
          <Box sx={{
            px: 3, py: 2,
            borderTop: '1px solid rgba(30,58,95,0.08)',
            display: 'flex', justifyContent: 'flex-end', gap: 1.5,
            flexShrink: 0,
            bgcolor: 'rgba(30,58,95,0.02)',
          }}>
            <Button
              onClick={onClose}
              sx={{
                color: '#5a6978', fontWeight: 500, fontSize: '0.875rem',
                px: 2.5, py: 1, borderRadius: '8px', textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(30,58,95,0.06)', color: navy },
              }}
            >
              ביטול
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
              sx={{
                bgcolor: navy, color: 'white',
                fontWeight: 600, fontSize: '0.875rem',
                px: 3.5, py: 1, borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(30,58,95,0.2)',
                minWidth: 120,
                '&:hover': { bgcolor: '#152a47', boxShadow: '0 4px 16px rgba(30,58,95,0.35)' },
                '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' },
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                  שומר...
                </>
              ) : isEditMode ? 'עדכן רכיב' : 'הוסף רכיב'}
            </Button>
          </Box>

        </Box>
      </Dialog>
    </ThemeProvider>
  );
};

export default WidgetConfigurator;
